import { AnalyticsContext } from '@carvana/eventable-clickstream';
import {
  useEffectWithController,
  useLoadableState,
  usePromise
} from '@carvana/merch-react-hooks';
import { datadogRum } from '@datadog/browser-rum';
import { useContext, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import DeviceContext, { useIsMobile } from '~/context/common/DeviceContext';
import StaticContext from '~/context/common/StaticContext';
import TradeContext from '~/context/common/TradeContext';
import {
  useFavoritesIds,
  useIsLoggedIn,
  useUserId,
  useUserLocation
} from '~/context/common/UserContext';
import { RecommendedVehicle } from '~/contracts/vdp';
import { getBulkAvailabilityInfo } from '~/hooks/vdp/services';
import { getSearchRequestId, getVehicleDetails } from '~/selectors';
import { fetchRecommendations } from '~/services/vdp';
import { BrowserCookies } from '~/utilities';
import { generateErrorLogger, logError } from '~/utilities/loggers';

function useGetRecommendations(numVehiclesToReturn = 6) {
  const { data, error, isLoaded, setData, setError, setLoading } =
    useLoadableState<RecommendedVehicle[]>([]);
  const [localVehicles, setLocalVehicles] = useState<RecommendedVehicle[]>([]);
  const mounted = usePromise();
  const favoritesIds = useFavoritesIds();
  const location = useUserLocation();
  const searchRequestId = useSelector(getSearchRequestId); // maps browser's events to search API's events
  const isMobile = useIsMobile();
  const { browserName } = useContext(DeviceContext);
  const { clientId, eventCorrelationId, referrer } = useContext(StaticContext);
  const { isTradeInApplied } = useContext(TradeContext);
  const { trackEvent } = useContext(AnalyticsContext);
  const isLoggedIn = useIsLoggedIn();
  const userId = useUserId();
  const {
    make = '',
    model = '',
    vehicleId = 0
  } = useSelector(getVehicleDetails);

  useEffectWithController(
    controller => {
      async function fetchVehicles() {
        setLoading();
        let vehicles = await fetchRecommendations(
          {
            analyticsData: {
              correlationId: eventCorrelationId,
              searchRequestId,
              isMobileDevice: isMobile,
              browser: browserName,
              filterSource: 'notSpecified',
              referrer
            },
            browserCookieId: BrowserCookies.get('BrowserCookieId') ?? '',
            filters: {},
            clientId,
            numVehiclesToReturn,
            vehicleId,
            zip5: location.zip
          },
          isLoggedIn,
          controller
        );

        if (vehicles.length === 0) return [];

        try {
          const deliveryData = await getBulkAvailabilityInfo({
            clientId,
            correlationId: eventCorrelationId,
            userId,
            vehicles,
            zip: location.zip
          });

          return vehicles.map(vehicle => ({
            ...vehicle,
            ...deliveryData.find(
              vehicleData => vehicleData.vehicleId === vehicle.vehicleId
            )
          }));
        } catch (err) {
          logError(
            err,
            { location: 'Get Bulk Availability Info' },
            { controller }
          );

          return vehicles;
        }
      }

      mounted(fetchVehicles())
        .then(vehicles => {
          setLocalVehicles(vehicles);
          if (vehicles.length === 0) {
            trackEvent('VYML - No Recommendations');
            datadogRum.addAction('VYML- No Recommendations');
            setData([]);
          }
        })
        .catch(
          generateErrorLogger(
            'Get recommended vehicles',
            {},
            { callback: setError, controller }
          )
        );
    },
    [
      browserName,
      clientId,
      eventCorrelationId,
      isLoggedIn,
      isMobile,
      isTradeInApplied,
      location,
      make,
      model,
      mounted,
      numVehiclesToReturn,
      referrer,
      searchRequestId,
      setData,
      setError,
      setLoading,
      trackEvent,
      userId,
      vehicleId
    ]
  );

  useEffect(() => {
    if (localVehicles.length === 0) return;

    setData(
      localVehicles.map<RecommendedVehicle>(vehicle => ({
        ...vehicle,
        isFavorited: favoritesIds.includes(vehicle.vehicleId)
      }))
    );
  }, [favoritesIds, localVehicles, setData]);

  return {
    error,
    isLoaded,
    vehicles: data
  };
}

export default useGetRecommendations;
